import { Dialog } from '@base-ui/react/dialog';
import {
  IconAlertTriangle,
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconRefresh,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { formatNumber, formatPercentage } from '../../i18n/format';
import { useTranslation } from '../../i18n/LocaleProvider';
import { DUR, PRESS } from '../../interaction';
import { cn } from '../../lib/cn';
import { Button } from '../core/Button';
import { IconButton } from '../core/IconButton';
import { Spinner } from '../core/Spinner';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// pdf.js parses off the main thread, so the worker has to be pointed at the copy
// the bundler emits. This is the only module in the library that renders
// `<Document>`, so it is the only place this is set.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export interface PdfPreviewProps {
  /** URL of the PDF to render. */
  src: string;
  /** Accessible name of the dialog, rendered as its heading. */
  title: string;
  open: boolean;
  onClose: () => void;
  /** Controlled zoom (1 = 100%). Pass with `onZoomChange` to own the value. */
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  /** Controlled page number. Pass with `onPageChange` to own the value. */
  page?: number;
  onPageChange?: (page: number) => void;
  /** File name the download link suggests. Falls back to the URL's own name. */
  downloadFileName?: string;
  className?: string;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
/** Width the page is laid out at when the modal is wide enough for it. */
const BASE_PAGE_WIDTH = 720;
/** Horizontal padding of the scroll body (`p-4` on both edges). */
const BODY_PADDING = 32;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * A modal that renders a real PDF with `react-pdf`.
 *
 * `react-pdf` rather than an `<iframe>` because zoom, page navigation and
 * download all have to be driven from the page — a browser's own viewer exposes
 * none of them to us. `suspense={false}` on `<Document>` keeps loading and
 * failure inside this component instead of throwing to an error boundary, which
 * is what makes the retry path below possible.
 */
export function PdfPreview({
  src,
  title,
  open,
  onClose,
  zoom,
  onZoomChange,
  page,
  onPageChange,
  downloadFileName,
  className,
}: PdfPreviewProps) {
  const reduceMotion = useReducedMotion();
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();
  const [attempt, setAttempt] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  const [internalZoom, setInternalZoom] = useState(1);
  const [internalPage, setInternalPage] = useState(1);

  // Both props must be present for the caller to own the value; with neither,
  // the modal keeps it in state. There is never a second copy of a controlled
  // value.
  const zoomControlled = zoom !== undefined && onZoomChange !== undefined;
  const currentZoom = clamp(zoomControlled ? zoom : internalZoom, MIN_ZOOM, MAX_ZOOM);

  const pageControlled = page !== undefined && onPageChange !== undefined;
  const pageCount = numPages > 0 ? numPages : 1;
  const currentPage = clamp(pageControlled ? page : internalPage, 1, pageCount);

  const setZoom = useCallback(
    (next: number) => {
      const value = clamp(next, MIN_ZOOM, MAX_ZOOM);
      if (!zoomControlled) setInternalZoom(value);
      onZoomChange?.(value);
    },
    [zoomControlled, onZoomChange],
  );

  const setPage = useCallback(
    (next: number) => {
      const value = clamp(next, 1, pageCount);
      if (!pageControlled) setInternalPage(value);
      onPageChange?.(value);
    },
    [pageControlled, onPageChange, pageCount],
  );

  const retry = useCallback(() => {
    setFailure(null);
    setNumPages(0);
    setAttempt((value) => value + 1);
  }, []);

  // A different document invalidates everything the previous one reported —
  // its page count, its failure and its pending retry.
  useEffect(() => {
    setAttempt(0);
    setNumPages(0);
    setFailure(null);
    setInternalPage(1);
  }, [src]);

  // Measuring in a layout effect keeps the first painted page at the width it
  // will keep, instead of reflowing once the observer reports.
  useLayoutEffect(() => {
    const element = bodyRef.current;
    if (!element || !open) return;

    const measure = () => setBodyWidth(element.clientWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [open]);

  // The page shrinks to the modal but never grows past its natural width; above
  // 100% zoom the body scrolls rather than the modal overflowing.
  const fitWidth = bodyWidth > 0 ? Math.max(bodyWidth - BODY_PADDING, 240) : BASE_PAGE_WIDTH;
  const pageWidth = Math.round(Math.min(fitWidth, BASE_PAGE_WIDTH) * currentZoom);

  const status: 'loading' | 'error' | 'ready' =
    failure !== null ? 'error' : numPages > 0 ? 'ready' : 'loading';

  const surfaceTransition = reduceMotion ? { duration: 0 } : { duration: DUR.fast };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      modal
    >
      <Dialog.Portal>
        <Dialog.Backdrop
          className={cn(
            'fixed inset-0 z-[var(--z-overlay)] bg-overlay backdrop-blur-sm',
            'transition-[opacity,backdrop-filter] duration-[var(--dur-base)] ease-out-soft',
            'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
          )}
        />

        {/* The shell animates through Base UI's own transition hooks rather than
            Motion: the popup has to stay mounted through the exit for the
            closing animation to play at all, and Base UI only keeps it there
            while a CSS transition is running on it (`getAnimations()`), which an
            inline style from Motion would override. Motion drives the states
            inside the body, where it owns the element outright. */}
        <Dialog.Popup
          className={cn(
            'fixed inset-0 z-[var(--z-modal)] m-auto flex h-[min(46rem,calc(100dvh-2rem))] w-[min(62rem,calc(100vw-2rem))]',
            'flex-col overflow-hidden rounded-xs border-2 border-line-strong bg-surface shadow-hard',
            'transition-[opacity,scale,transform] duration-[var(--dur-base)] ease-spring',
            'data-[starting-style]:scale-[0.97] data-[ending-style]:scale-[0.98]',
            'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
            className,
          )}
        >
          <header className="flex shrink-0 items-center gap-2 border-b-2 border-line-strong bg-surface-alt px-3 py-2">
            <Dialog.Title className="min-w-0 flex-1 truncate font-display text-sm font-semibold tracking-title text-heading uppercase">
              {title}
            </Dialog.Title>

            <IconButton
              label={t('ds.pdf.zoomOut')}
              icon={<IconZoomOut />}
              size="sm"
              variant="outline"
              disabled={currentZoom <= MIN_ZOOM}
              onClick={() => setZoom(currentZoom - ZOOM_STEP)}
            />
            <span
              aria-live="polite"
              className="w-12 shrink-0 text-center font-mono text-2xs text-body tabular-nums"
            >
              {formatPercentage(currentZoom * 100, { precision: 0 })}
            </span>
            <IconButton
              label={t('ds.pdf.zoomIn')}
              icon={<IconZoomIn />}
              size="sm"
              variant="outline"
              disabled={currentZoom >= MAX_ZOOM}
              onClick={() => setZoom(currentZoom + ZOOM_STEP)}
            />
            <IconButton
              label={t('ds.pdf.resetZoom')}
              icon={<IconRefresh />}
              size="sm"
              variant="ghost"
              onClick={() => setZoom(1)}
            />

            <a
              href={src}
              download={downloadFileName ?? ''}
              className={cn(
                'dhd-target relative inline-flex h-8 shrink-0 select-none items-center gap-1.5 rounded-xs px-3',
                'border-2 border-line-strong font-display text-2xs font-semibold tracking-label text-heading uppercase',
                'transition-[background-color,border-color] duration-[var(--dur-fast)] ease-spring',
                'hover:border-heading hover:bg-surface-muted',
                PRESS,
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
              )}
            >
              <IconDownload aria-hidden="true" className="size-3.5 shrink-0" />
              Download
            </a>

            <Dialog.Close
              render={<IconButton label="Close preview" icon={<IconX />} size="sm" variant="ghost" />}
            />
          </header>

          <div
            ref={bodyRef}
            // The page itself is text, so the scrolling region has no focusable
            // descendant to reach it by keyboard: it takes focus itself.
            tabIndex={0}
            aria-busy={status === 'loading' ? true : undefined}
            className={cn(
              'relative min-h-0 flex-1 overflow-auto bg-surface-muted',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            )}
          >
            <div className="flex min-h-full w-full flex-col items-center justify-center p-4">
              {/* Out of flow so the surface leaving never drags the page with
                  it, and transparent to the pointer so it cannot swallow a
                  click meant for the page underneath. */}
              <div className="pointer-events-none absolute inset-0 grid place-items-center p-4">
                <AnimatePresence mode="wait" initial={false}>
                {status === 'loading' ? (
                  <motion.div
                    key="loading"
                    initial={reduceMotion ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={surfaceTransition}
                    className="flex flex-col items-center gap-3"
                  >
                    <Spinner size="lg" tone="primary" label={t('ds.pdf.loadingLabel')} />
                    <p className="text-xs text-muted">{t('ds.pdf.loading')}</p>
                  </motion.div>
                ) : status === 'error' ? (
                  <motion.div
                    key="error"
                    role="alert"
                    initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={surfaceTransition}
                    className="pointer-events-auto flex w-full max-w-md flex-col items-center gap-3 rounded-xs border-2 border-danger bg-danger-surface p-6 text-center"
                  >
                    <IconAlertTriangle aria-hidden="true" className="size-8 text-danger" />
                    <p className="font-display text-sm font-semibold tracking-title text-heading uppercase">
                      {t('ds.pdf.errorTitle')}
                    </p>
                    <p className="text-sm text-body">{failure}</p>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        leadingIcon={<IconRefresh />}
                        onClick={retry}
                      >
                        Retry
                      </Button>
                      <Button size="sm" variant="outline" onClick={onClose}>
                        Close
                      </Button>
                    </div>
                  </motion.div>
                ) : null}
                </AnimatePresence>
              </div>

              {/* Remounted on retry (and on a new `src`) so a failed load is
                  genuinely attempted again rather than re-read from state. */}
              <div key={`${src}-${attempt}`} className={cn('mx-auto', status !== 'ready' && 'hidden')}>
                <Document
                  key={`${src}-${attempt}`}
                  file={src}
                  className="flex flex-col items-center"
                  // `suspense={false}` routes loading and failure through these
                  // two props instead of the nearest error boundary. Both are
                  // rendered by this component's own status surfaces, so the
                  // document itself renders nothing for either state.
                  loading={null}
                  error={null}
                  suspense={false}
                  onLoadSuccess={({ numPages: total }) => {
                    setNumPages(total);
                    setFailure(null);
                  }}
                  onLoadError={(error) => {
                    setFailure(error.message || 'The document could not be loaded.');
                  }}
                >
                  <Page
                    pageNumber={currentPage}
                    width={pageWidth}
                    renderTextLayer
                    renderAnnotationLayer
                    loading={null}
                    error={null}
                    className="shadow-lg"
                  />
                </Document>
              </div>
            </div>
          </div>

          {numPages > 1 ? (
            <footer className="flex shrink-0 items-center gap-2 border-t-2 border-line-strong bg-surface-alt px-3 py-2">
              <IconButton
                label={t('ds.pdf.previousPage')}
                icon={<IconChevronLeft />}
                size="sm"
                variant="outline"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              />
              <span aria-live="polite" className="font-mono text-2xs text-body tabular-nums">
                {t('ds.pdf.pageOf', {
                  current: formatNumber(currentPage),
                  total: formatNumber(numPages),
                })}
              </span>
              <IconButton
                label={t('ds.pdf.nextPage')}
                icon={<IconChevronRight />}
                size="sm"
                variant="outline"
                disabled={currentPage >= numPages}
                onClick={() => setPage(currentPage + 1)}
              />
            </footer>
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
